/* eslint-disable @typescript-eslint/no-unused-vars */
import { Form, Input } from 'antd';
import { useForm } from 'antd/lib/form/Form';

import styles from './index.module.scss';
import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import {
  IAPICompanyFullInfo,
  ICertInfo,
  ICreateInternalCompanyBody,
} from '../../../../model/api-modules';
import { IRootState } from '../../../../store/reducers';
import { safeParseJson, strToJson } from '../../../../lib';
import { DeployArea, ICompanyRejectReason } from '../../../../model';
import {
  createInternalCompanyInfo,
  getKpiCodeList,
  updateInternalCompanyInfo,
} from '../../../../api';
import getFormatMessage from '../../../../locales/get-format-message';
import { ConsoleDrawer } from '../../../../components/drawer';
import { ConsoleForm } from '../../../../components/form';
import { CommonInput } from '../../../../components/input';
import { CommonSelect } from '../../../../components/select';
import { getConfig } from '../../../../config';

interface IProps {
  kpiCodeOptions: { label: string; value: string }[];
  data?: IAPICompanyFullInfo | null;

  onOK?: () => void;
  onClose: () => void;
}

interface IRejectReasonMap {
  [key: string]: string;
}

interface IInternalFormInfo {
  projectName: string;
  projectOwner: string;
  productCode: string;
  kpiCodeName?: string;
}

export const CompanyInternalAuthDrawer = (props: IProps) => {
  const { data: companyInfo, kpiCodeOptions, onClose } = props;

  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );

  const [form] = useForm<IInternalFormInfo>();

  const [rejectReasons, setRejectReasons] = useState<IRejectReasonMap>({});

  useEffect(() => {
    init();
  }, [companyInfo?.id]);

  const init = async () => {
    // 回填数据
    const formInfo = companyInfo?.InternalCertInfo || {};
    form.setFieldsValue(
      Object.assign(formInfo, {
        kpiCodeName: companyInfo?.InternalCertInfo?.productName || '',
      })
    );

    // 设置驳回原因
    const rejectItems =
      strToJson<ReadonlyArray<ICompanyRejectReason>>(
        companyInfo?.RejectReason || ''
      ) || [];

    const rejectMap = rejectItems.reduce<IRejectReasonMap>((acc, item) => {
      if (item.type) {
        acc[item.type] = item.reason;
      }
      return acc;
    }, {});
    setRejectReasons(rejectMap);
  };

  const submit = async () => {
    if (!selectStudio) {
      return false;
    }
    const formData = await form.validateFields();
    const params: ICreateInternalCompanyBody = {
      projectName: formData.projectName.trim(),
      projectOwner: formData.projectOwner.trim(),
      kpiCode: formData.productCode.trim(),
      studioID: selectStudio.studioID,
    };
    if (getConfig().deployArea === DeployArea.singapore) {
      params.kpiCodeName = formData.kpiCodeName?.trim() || '';
    }
    let res = false;
    if (companyInfo?.InternalCertInfo) {
      res = await updateInternalCompanyInfo(params);
    } else {
      res = await createInternalCompanyInfo(params);
    }
    if (res) {
      await props.onOK?.();
      return true;
    }
    return false;
  };

  const getErrTips = (type: string) => {
    if (!rejectReasons[type]) {
      return;
    }
    return (
      <>
        {!!rejectReasons[type] && (
          <div className={styles.errTips}>{rejectReasons[type]}</div>
        )}
      </>
    );
  };

  return (
    <ConsoleDrawer
      title={getFormatMessage('studio_auth_internal')}
      onOk={submit}
      onClose={onClose}
    >
      <ConsoleForm form={form}>
        <Form.Item
          label={getFormatMessage('studio_auth_project_name')}
          name="projectName"
          rules={[
            {
              required: true,
              validator: (rule, value, callback) => {
                if (!value || value.trim() === '') {
                  callback(
                    getFormatMessage('please_enter_field', {
                      params: {
                        name: getFormatMessage('studio_auth_project_name'),
                      },
                    })
                  );
                  return;
                }
                if (value?.length > 64) {
                  callback(getFormatMessage('company_input_wrong'));
                  return;
                }
                callback();
              },
            },
          ]}
          extra={getErrTips('projectName')}
        >
          <CommonInput fullWidth />
        </Form.Item>

        <Form.Item
          label={getFormatMessage('studio_auth_project_owner')}
          name="projectOwner"
          extra={getErrTips('projectOwner')}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <CommonInput fullWidth maxLength={50} />
        </Form.Item>

        {getConfig().deployArea === DeployArea.singapore && (
          <Form.Item
            label={getFormatMessage('KPI Name')}
            name="kpiCodeName"
            extra={getErrTips('productCode')}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <CommonInput fullWidth />
          </Form.Item>
        )}

        <Form.Item
          label={getFormatMessage('KPI Code')}
          name="productCode"
          rules={[
            {
              required: true,
            },
          ]}
          extra={getErrTips('productCode')}
        >
          {getConfig().deployArea === DeployArea.singapore ? (
            <CommonInput fullWidth />
          ) : (
            <CommonSelect showSearch options={kpiCodeOptions} virtual={true} />
          )}
        </Form.Item>
      </ConsoleForm>
    </ConsoleDrawer>
  );
};
